import {
  StargraveEquipment,
  StargraveAdvancedWeapon,
  StargraveAlienArtefact,
  StargravePower,
  StargraveShipUpgrade,
  StargraveSoldierProfile,
  StargraveTechnology,
} from '../types';
import { stargravePowers } from '../data/stargrave/coreRules';
import { getAllStargraveEquipment, getAllStargraveSoldiers } from '../stores/stargraveCustomStore';
import { matchesIndexQuery } from './indexSearch';
import { stargraveAdvancedTechnology } from '../data/stargrave/advancedTechnology';
import { stargraveShipUpgrades } from '../data/stargrave/shipUpgrades';
import { stargraveAdvancedWeapons } from '../data/stargrave/advancedWeapons';
import { stargraveAlienArtefacts } from '../data/stargrave/alienArtefacts';

export type StargraveIndexCategory = 'powers' | 'soldiers' | 'equipment' | 'advancedWeapons' | 'technology' | 'artefacts' | 'ship';

/** Tab-level filter — 'special' spans the three advanced-content categories. */
export type StargraveIndexFilter = StargraveIndexCategory | 'special' | 'all';

const SPECIAL_CATEGORIES: StargraveIndexCategory[] = ['advancedWeapons', 'technology', 'artefacts'];

export type StargraveIndexEntry =
  | { category: 'powers'; id: string; item: StargravePower; searchText: string }
  | { category: 'soldiers'; id: string; item: StargraveSoldierProfile; searchText: string }
  | { category: 'equipment'; id: string; item: StargraveEquipment; searchText: string }
  | { category: 'advancedWeapons'; id: string; item: StargraveAdvancedWeapon; searchText: string }
  | { category: 'artefacts'; id: string; item: StargraveAlienArtefact; searchText: string }
  | { category: 'technology'; id: string; item: StargraveTechnology; searchText: string }
  | { category: 'ship'; id: string; item: StargraveShipUpgrade; searchText: string };

const byName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((left, right) => left.name.localeCompare(right.name));

export function getStargraveIndexEntries(): StargraveIndexEntry[] {
  return [
  ...byName(stargravePowers).map((item): StargraveIndexEntry => ({
    category: 'powers', id: item.id, item,
    searchText: `${item.name} ${item.timing} ${item.summary} activation ${item.activation} strain ${item.strain}`.toLowerCase(),
  })),
  ...byName(getAllStargraveSoldiers()).map((item): StargraveIndexEntry => ({
    category: 'soldiers', id: item.id, item,
    searchText: `${item.name} ${item.category} ${item.notes ?? ''} ${item.equipmentIds.join(' ')}`.toLowerCase(),
  })),
  ...byName(getAllStargraveEquipment()).map((item): StargraveIndexEntry => ({
    category: 'equipment', id: item.id, item,
    searchText: `${item.name} ${item.category} ${item.notes ?? ''}`.toLowerCase(),
  })),
  ...byName(stargraveAdvancedTechnology).map((item): StargraveIndexEntry => ({
    category: 'technology', id: item.id, item,
    searchText: `${item.name} ${item.summary} ${item.restriction ?? ''}`.toLowerCase(),
  })),
  ...byName(stargraveAdvancedWeapons).map((item): StargraveIndexEntry => ({
    category: 'advancedWeapons', id: item.id, item,
    searchText: `${item.name} ${item.baseWeaponId} ${item.summary} indestructible`.toLowerCase(),
  })),
  ...byName(stargraveAlienArtefacts).map((item): StargraveIndexEntry => ({
    category: 'artefacts', id: item.id, item,
    searchText: `${item.name} ${item.summary} alien artefact`.toLowerCase(),
  })),
  ...byName(stargraveShipUpgrades).map((item): StargraveIndexEntry => ({
    category: 'ship', id: item.id, item,
    searchText: `${item.name} ${item.summary}`.toLowerCase(),
  })),
  ];
}

export function searchStargraveIndex(
  query: string,
  category: StargraveIndexFilter = 'all'
): StargraveIndexEntry[] {
  return getStargraveIndexEntries().filter((entry) =>
    (category === 'all'
      || (category === 'special' ? SPECIAL_CATEGORIES.includes(entry.category) : entry.category === category))
    && matchesIndexQuery(entry.searchText, query)
  );
}
